import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RemitterTypeEnum, RemitterTypes } from '@pg-app/client/payment/constants/remitter-type';
import { CommonDialogComponent } from '@pg-app/common/common-dialog/common-dialog.component';
import { ActionTypes } from '@pg-app/shared/constants/action-types-constants';
import { MiscConstants } from '@pg-app/shared/constants/misc-constants';
import { Regex } from '@pg-app/shared/constants/regex';
import { format } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import { ManageUltimateRemittersService } from '../../manage-ultimate-remitters.service';

@Component({
  selector: 'np-single-ultimate-remitter',
  templateUrl: './single-ultimate-remitter.component.html',
  styleUrls: ['./single-ultimate-remitter.component.scss']
})
export class SingleUltimateRemitterComponent implements OnInit {
  currentForm: FormGroup;
  types = RemitterTypes;
  countries = [];

  titleSuffix = ``;
  isNew : boolean;

  isIndividual = false;
  isCompany = false;

  get idCtrl(): FormControl {
    return this.currentForm.get('id') as FormControl;
  }

  get typeCtrl(): FormControl {
    return this.currentForm.get('type') as FormControl;
  }

  get firstNameCtrl(): FormControl {
    return this.currentForm.get('firstName') as FormControl;
  }

  get lastNameCtrl(): FormControl {
    return this.currentForm.get('lastName') as FormControl;
  }

  get companyNameCtrl(): FormControl {
    return this.currentForm.get('companyName') as FormControl;
  }

  get companyNumberCtrl(): FormControl {
    return this.currentForm.get('companyNumber') as FormControl;
  }

  get dateOfBirthCtrl(): FormControl {
    return this.currentForm.get('dateOfBirth') as FormControl;
  }

  get addressCtrl(): FormControl {
    return this.currentForm.get('address') as FormControl;
  }

  get cityCtrl(): FormControl {
    return this.currentForm.get('city') as FormControl;
  }

  get postCodeCtrl(): FormControl {
    return this.currentForm.get('postCode') as FormControl;
  }

  get stateProvinceCtrl(): FormControl {
    return this.currentForm.get('stateProvince') as FormControl;
  }

  get countryCtrl(): FormControl {
    return this.currentForm.get('country') as FormControl;
  }

  get leadCtrl(): FormControl {
    return this.currentForm.get('leadId') as FormControl;
  }

  get useForTreasuryPaymentsCtrl(): FormControl {
    return this.currentForm.get('useForTreasuryPayments') as FormControl;
  }

  constructor(
    private fb: FormBuilder,
    private manageUltimateRemittersService: ManageUltimateRemittersService,
    private toastrService: ToastrService,
    public dialogRef: MatDialogRef<SingleUltimateRemitterComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData,
    private dialog: MatDialog) { }

  ngOnInit(): void {
    this.currentForm = this.fb.group({
      id: [this.dialogData.ultimateRemitter?.id],
      type: [this.dialogData.ultimateRemitter?.type, [Validators.required]],
      companyName: [this.dialogData.ultimateRemitter?.companyName],
      companyNumber: [this.dialogData.ultimateRemitter?.companyNumber],
      firstName: [this.dialogData.ultimateRemitter?.firstName],
      lastName: [this.dialogData.ultimateRemitter?.lastName],
      dateOfBirth: [this.dialogData.ultimateRemitter?.dateOfBirth],
      address: [this.dialogData.ultimateRemitter?.address, [Validators.required, Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]],
      city: [this.dialogData.ultimateRemitter?.city, [Validators.required, Validators.maxLength(50), Validators.pattern(Regex.nonSwiftCharacters)]],
      postCode: [this.dialogData.ultimateRemitter?.postCode, [Validators.required, Validators.maxLength(12), Validators.pattern(Regex.nonSwiftCharacters)]],
      stateProvince: [this.dialogData.ultimateRemitter?.stateProvince, [Validators.maxLength(50), Validators.pattern(Regex.nonSwiftCharacters)]],
      country: [this.dialogData.ultimateRemitter?.countryName, [Validators.required]],
      leadId: [this.dialogData.ultimateRemitter?.leadId],
      useForTreasuryPayments: [this.dialogData.ultimateRemitter?.useForTreasuryPayments ?? false, [Validators.required]],
    })

    this.countries = this.dialogData.countries;

    this.typeChanged();
    this.changeRemitterCountry();

    if (!this.dialogData.ultimateRemitter?.id) {
      this.titleSuffix = `(New)`;
      this.isNew = true;
    } else {
      this.isNew = false;
    }
  }

  changeRemitterCountry(){
    const remitterCountryValue = this.countryCtrl.value;
    const country = ['United States', 'Canada', 'Australia'];
    if(country.includes(remitterCountryValue)){
      this.stateProvinceCtrl.setValidators([Validators.required, Validators.maxLength(50)]);
    }
    else{
      this.stateProvinceCtrl.setValidators([Validators.maxLength(50)]);
    }
    this.stateProvinceCtrl.updateValueAndValidity();
  }

  typeChanged() {
    const type = this.typeCtrl.value;

    if (!type) {
      this.isIndividual = false;
      this.isCompany = false;
      return;
    }

    if (type === RemitterTypeEnum.Individual) {
      this.isIndividual = true;
      this.isCompany = false;

      this.firstNameCtrl.setValidators([Validators.required, Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]);
      this.lastNameCtrl.setValidators([Validators.required, Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]);
      this.dateOfBirthCtrl.setValidators([Validators.required]);

      this.companyNameCtrl.setValidators([Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]);
      this.companyNumberCtrl.setValidators([Validators.pattern(Regex.nonSwiftCharacters)]);
    } else if (type === RemitterTypeEnum.Company) {
      this.isIndividual = false;
      this.isCompany = true;

      this.firstNameCtrl.setValidators([Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]);
      this.lastNameCtrl.setValidators([Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]);
      this.dateOfBirthCtrl.setValidators([]);

      this.companyNameCtrl.setValidators([Validators.required, Validators.pattern(Regex.nonSwiftCharacters), Validators.maxLength(35)]);
      this.companyNumberCtrl.setValidators([Validators.required, Validators.pattern(Regex.nonSwiftCharacters)]);
    } else {
      this.isIndividual = false;
      this.isCompany = false;

      this.toastrService.error(`Type unrecognised`);
    }

    this.firstNameCtrl.updateValueAndValidity();
    this.lastNameCtrl.updateValueAndValidity();
    this.dateOfBirthCtrl.updateValueAndValidity();
    this.companyNameCtrl.updateValueAndValidity();
    this.companyNumberCtrl.updateValueAndValidity();
  }

  save() {
    this.currentForm.markAllAsTouched();
    if (!this.currentForm.valid) {
      return;
    }

    const countryName = this.countryCtrl.value;
    const selectedCountry = this.countries.find(x => x.name.toLowerCase() === countryName.toLowerCase());

    if (!selectedCountry) {
      this.toastrService.error(`Cannot find country ${countryName}`);
      return;
    }

    const param = {
      id: this.idCtrl.value,
      type: this.typeCtrl.value,
      firstName: this.firstNameCtrl.value,
      lastName: this.lastNameCtrl.value,
      dateOfBirthString: format(this.dateOfBirthCtrl.value, 'YYYYMMDD'),
      companyName: this.companyNameCtrl.value,
      companyNumber: this.companyNumberCtrl.value,
      address: this.addressCtrl.value,
      city: this.cityCtrl.value,
      postCode: this.postCodeCtrl.value,
      stateProvince: this.stateProvinceCtrl.value,
      countryId: selectedCountry.id,
      leadId: this.leadCtrl.value,
      useForTreasuryPayments: this.useForTreasuryPaymentsCtrl.value
    };

    if (param.id) {
      this.manageUltimateRemittersService.updateUltimateRemitter(param).subscribe(
        result => {
          if (result.isSucceeded) {
            this.dialogRef.close(ActionTypes.Updating);
          } else {
            this.toastrService.error(result.error);
          }
        }
      );
    } else {
      param.id = MiscConstants.emptyGuid;
      param.leadId = MiscConstants.emptyGuid;

      this.manageUltimateRemittersService.insertUltimateRemitter(param).subscribe(
        result => {
          if (result.isSucceeded) {
            this.dialogRef.close(ActionTypes.Adding);
          } else {
            this.toastrService.error(result.error);
          }
        }
      );
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  delete() {
    const dialogRef = this.dialog.open(CommonDialogComponent, {
      minWidth: '300px',
      disableClose: true,
      data: {
        title: 'Are you sure you want to delete this ultimate remitter?',
        dialogType: 3,
        routingUrl: null
      }
    });

    dialogRef.afterClosed().subscribe(dlgResult => {
      if (dlgResult) {
        this.toDelete();
      }
    });
  }

  private toDelete() {
    const param = {
      id: this.idCtrl.value
    };

    this.manageUltimateRemittersService.deleteUltimateRemitter(param).subscribe(
      result => {
        if (result.isSucceeded) {
          this.dialogRef.close(ActionTypes.Deleting);
        } else {
          this.toastrService.error(result.error);
        }
      });
  }
}
